import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight, Lock } from 'lucide-react';
type Preview = {
  src: string;
  label: string;
  caption: string;
};
const PREVIEWS: Preview[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/govoq7pVoPCopDTjjtj6E6/IMG_1873.jpg',
  label: 'Sistema organizado',
  caption: 'Índice del Tomo 1 — 7 secciones'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/x1icAa1n2EA4pWA1LGhqkt/IMG_1874.jpg',
  label: 'Razonamiento clínico',
  caption: 'Pensamiento hipotético-deductivo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/ppHNgcNsadR1qovGm7XfAD/IMG_1871.jpg',
  label: 'Anatomía funcional',
  caption: 'Anatomía funcional de la cadera'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6SdybWKmrRUboKBUxZRVVg/IMG_1869.jpg',
  label: 'Biomecánica clínica',
  caption: 'Segmentos vertebrales y curvaturas'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fndTKCNKeovaj5ugBYgd6f/IMG_1875.jpg',
  label: 'Movilidad articular',
  caption: 'Rangos normales por articulación'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/iKtEG8XHpEQMZ4mKgRqrKj/IMG_1876.jpg',
  label: 'Evaluación funcional',
  caption: 'Escala de Daniels paso a paso'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/7TNPgFEAqg9XeAg1erKnxB/IMG_1870.jpg',
  label: 'Red flags clínicas',
  caption: '5D y 3N — alarmas cervicales'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/twFsDqHpBiGjkSA9XaeUMe/IMG_1877.jpg',
  label: 'Diagnóstico diferencial',
  caption: 'Columna lumbar — 4 cuadros'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/7r8XEQJTF7HsSVWZM1o5XR/IMG_1878.jpg',
  label: 'Pruebas ortopédicas',
  caption: 'Muñeca y mano — diferencial'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/3b1RRd7nWcBevXDdB1B4eg/IMG_1868.jpg',
  label: 'Tomos organizados',
  caption: 'Índice del Tomo 3 — Columna'
}];
export function ContentPreview() {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };
  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, {
      passive: true
    });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, []);
  const scrollByCard = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-preview-card]');
    const step = card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({
      left: dir * step,
      behavior: 'smooth'
    });
  };
  return <section id="content-preview" className="relative overflow-hidden bg-navy-950 border-y border-white/5 py-12 sm:py-20 md:py-24" data-id="element-42">
      {/* Subtle grid texture */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" data-id="element-43">
        <div className="absolute inset-0" style={{
        backgroundImage: 'linear-gradient(rgba(201,168,76,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.4) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} data-id="element-44" />
      </div>

      {/* Ambient glow */}
      <div className="absolute top-1/3 -left-32 w-[500px] h-[500px] bg-gold/[0.05] blur-[120px] rounded-full pointer-events-none" data-id="element-45"></div>
      <div className="absolute bottom-1/3 -right-32 w-[500px] h-[500px] bg-emerald-500/[0.04] blur-[120px] rounded-full pointer-events-none" data-id="element-46"></div>

      {/* Header */}
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-12" data-id="element-47">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gold/30 bg-gold/5 text-gold text-[9px] sm:text-xs font-bold tracking-[0.15em] uppercase mb-4 sm:mb-5" data-id="element-48">
          <span className="w-1 h-1 rounded-full bg-gold" data-id="element-49"></span>
          <span data-id="element-50">Solo una muestra del interior</span>
        </motion.div>

        <motion.h2 initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.05
      }} className="font-heading font-extrabold text-white leading-[1.05] tracking-tight text-3xl sm:text-4xl md:text-5xl mb-3 sm:mb-4 max-w-3xl" data-id="element-51">
          Esto es{' '}
          <span className="font-editorial italic text-gold font-medium" data-id="element-52">
            solo algo
          </span>{' '}
          de lo que vas a encontrar adentro
        </motion.h2>

        <motion.p initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="text-sm sm:text-base md:text-lg text-gray-400 leading-relaxed max-w-2xl" data-id="element-53">
          Una vista previa real del sistema clínico. El Mega Pack incluye{' '}
          <span className="text-white font-semibold" data-id="element-54">mucho más</span>: 6 tomos
          completos con anamnesis, pruebas ortopédicas, casos clínicos, red
          flags y razonamiento clínico aplicado.
        </motion.p>
      </div>

      {/* Scroller */}
      <div className="relative" data-id="element-55">
        {/* Edge fades */}
        <div className="hidden md:block pointer-events-none absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-navy-950 to-transparent z-10" data-id="element-56" />
        <div className="hidden md:block pointer-events-none absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-navy-950 to-transparent z-10" data-id="element-57" />

        {/* Prev / Next buttons (desktop) */}
        <div className="hidden md:flex absolute left-3 lg:left-6 top-1/2 -translate-y-1/2 z-20" data-id="element-58">
          <button onClick={() => scrollByCard(-1)} disabled={!canPrev} aria-label="Anterior" className={`w-11 h-11 rounded-full bg-navy-900/90 backdrop-blur border border-gold/30 text-gold hover:bg-gold hover:text-navy-950 transition-all flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.4)] ${canPrev ? 'opacity-100' : 'opacity-30 cursor-not-allowed'}`} data-id="element-59">
            <ChevronLeft size={20} strokeWidth={2.5} data-id="element-60" />
          </button>
        </div>
        <div className="hidden md:flex absolute right-3 lg:right-6 top-1/2 -translate-y-1/2 z-20" data-id="element-61">
          <button onClick={() => scrollByCard(1)} disabled={!canNext} aria-label="Siguiente" className={`w-11 h-11 rounded-full bg-navy-900/90 backdrop-blur border border-gold/30 text-gold hover:bg-gold hover:text-navy-950 transition-all flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.4)] ${canNext ? 'opacity-100' : 'opacity-30 cursor-not-allowed'}`} data-id="element-62">
            <ChevronRight size={20} strokeWidth={2.5} data-id="element-63" />
          </button>
        </div>

        <div ref={scrollerRef} className="scrollbar-hide overflow-x-auto scroll-smooth snap-x snap-mandatory" style={{
        scrollPaddingLeft: '1rem',
        scrollPaddingRight: '1rem',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-64">
          <div className="flex gap-4 sm:gap-6 px-4 sm:px-6 lg:px-12 pt-2 pb-6" data-id="element-65">
            {PREVIEWS.map((p, idx) => <motion.div key={idx} data-preview-card initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: idx * 0.06,
            duration: 0.5
          }} className="snap-start flex-shrink-0 w-[260px] sm:w-[300px] md:w-[340px] group" data-id="element-66">
                {/* Page frame */}
                <div className="relative" data-id="element-67">
                  {/* Soft glow behind card */}
                  <div className="absolute inset-0 bg-gold/[0.08] blur-2xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-68" />

                  <div className="relative rounded-2xl overflow-hidden bg-white border border-white/10 group-hover:border-gold/40 shadow-[0_20px_50px_rgba(0,0,0,0.5)] group-hover:shadow-[0_25px_60px_rgba(201,168,76,0.25)] transition-all duration-500 aspect-[3/4]" data-id="element-69">
                    <img src={p.src} alt={p.caption} width={680} height={907} loading="lazy" decoding="async" {...{
                  fetchpriority: 'low'
                } as any} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]" data-id="element-70" />

                    {/* Top gold accent strip */}
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" data-id="element-71" />

                    {/* Page number chip */}
                    <div className="absolute top-3 right-3 bg-navy-950/85 backdrop-blur-sm border border-white/10 rounded-full px-2.5 py-1 text-[10px] font-bold text-gold tracking-wider" data-id="element-72">
                      {String(idx + 1).padStart(2, '0')} /{' '}
                      {String(PREVIEWS.length).padStart(2, '0')}
                    </div>
                  </div>
                </div>

                {/* Label below */}
                <div className="mt-4 px-1" data-id="element-73">
                  <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold tracking-[0.15em] uppercase text-gold/80 mb-1" data-id="element-74">
                    <span className="w-3 h-px bg-gold/60" data-id="element-75" />
                    {p.label}
                  </div>
                  <p className="text-sm sm:text-[15px] text-white font-medium leading-snug" data-id="element-76">
                    {p.caption}
                  </p>
                </div>
              </motion.div>)}

            {/* Tail CTA card */}
            <motion.a data-preview-card href="#pricing" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: PREVIEWS.length * 0.06,
            duration: 0.5
          }} className="snap-start flex-shrink-0 w-[260px] sm:w-[300px] md:w-[340px] group" data-id="element-77">
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-gold/30 bg-gradient-to-b from-navy-900 to-navy-950 flex flex-col items-center justify-center text-center px-6 group-hover:border-gold/60 transition-all duration-500 shadow-[0_20px_50px_rgba(0,0,0,0.5)] group-hover:shadow-[0_25px_60px_rgba(201,168,76,0.3)]" data-id="element-78">
                <div className="absolute inset-0 bg-gold/[0.05] opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-79" />
                <div className="relative w-12 h-12 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center mb-4" data-id="element-80">
                  <Lock size={20} className="text-gold" strokeWidth={2} data-id="element-81" />
                </div>
                <p className="relative text-[11px] tracking-[0.18em] uppercase text-gold/80 font-bold mb-2" data-id="element-82">
                  + Mucho más
                </p>
                <h3 className="relative font-heading font-bold text-white text-lg sm:text-xl leading-tight mb-2" data-id="element-83">
                  El sistema completo te espera
                </h3>
                <p className="relative text-xs sm:text-sm text-gray-400 leading-relaxed mb-5" data-id="element-84">
                  6 tomos · 6 bonos · Casos clínicos · Pruebas ortopédicas
                </p>
                <span className="relative inline-flex items-center gap-2 bg-gold text-navy-950 text-xs sm:text-sm font-bold px-4 py-2 rounded-full group-hover:gap-3 transition-all" data-id="element-85">
                  Ver el pack completo
                  <ArrowRight size={14} strokeWidth={2.5} data-id="element-86" />
                </span>
              </div>
              <div className="mt-4 px-1" data-id="element-87">
                <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold tracking-[0.15em] uppercase text-gold/80 mb-1" data-id="element-88">
                  <span className="w-3 h-px bg-gold/60" data-id="element-89" />
                  Acceso completo
                </div>
                <p className="text-sm sm:text-[15px] text-white font-medium leading-snug" data-id="element-90">
                  Y mucho más adentro del Mega Pack
                </p>
              </div>
            </motion.a>
          </div>
        </div>

        {/* Mobile scroll hint */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="md:hidden flex items-center justify-center gap-1.5 text-[11px] text-gray-500 mt-2 px-4" data-id="element-91">
          <span data-id="element-92">Deslizá para ver más</span>
          <ChevronRight size={13} className="animate-pulse" data-id="element-93" />
        </motion.div>
      </div>
    </section>;
}